import React, { ReactElement, useContext, useEffect, useMemo } from "react";
import { useRef, useState } from "react";

import { Dialog, DialogContent, DialogTitle } from "@material-ui/core";
import { Alert } from "@material-ui/lab";

declare global {
  class CaptchaError extends Error {
    constructor(message: string);
    kind: "internal_error" | "network_error" | "token_error" | "client_error";
    statusCode?: number;
  }

  interface Window {
    AwsWafCaptcha: {
      renderCaptcha: (
        targetDiv: HTMLElement,
        options: {
          apiKey: string;
          onSuccess: (token: string) => void;
          onError: (err: CaptchaError) => void;
        }
      ) => void;
    };
    awsWafCookieDomainList: string[];
  }
}

window.awsWafCookieDomainList = [process.env.REACT_APP_CAPTCHA_DOMAIN!];

type CaptchaCallback = (token: string) => void;

type CaptchaContextValue = {
  showCaptcha: (cb: CaptchaCallback) => void;
  getLatestToken: () => string | null;
  getLatestTokenHeaders: () => Record<string, string>;
};

const CaptchaContext = React.createContext<CaptchaContextValue>({
  showCaptcha: () => undefined,
  getLatestToken: () => null,
  getLatestTokenHeaders: () => ({})
});

export function useCaptcha(): CaptchaContextValue {
  return useContext(CaptchaContext);
}

export default function CaptchaProvider({
  children
}: {
  children: React.ReactNode;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasRendered, setHasRendered] = useState(false);
  const latestToken = useRef<string | null>(
    localStorage.getItem("wafToken") || null
  );
  const onSuccessRef = useRef<CaptchaCallback | null>(null);
  const broadcastChannel = useRef<BroadcastChannel | null>(null);

  function onGetToken(wafToken: string) {
    setOpen(false);
    setError(null);
    setHasRendered(false);

    latestToken.current = wafToken;

    onSuccessRef.current?.(wafToken);
    onSuccessRef.current = null;
  }

  const ctxValue = useMemo(
    () => ({
      showCaptcha: (cb: CaptchaCallback) => {
        if (onSuccessRef.current) {
          console.warn("showCaptcha called while another captcha is open");
        }

        setOpen(true);
        setError(null);
        setHasRendered(false);

        //latestToken.current = null;
        onSuccessRef.current = cb;
      },
      getLatestToken: () => latestToken.current,
      getLatestTokenHeaders: () =>
        latestToken.current
          ? { "x-aws-waf-token": latestToken.current }
          : ({} as Record<string, string>)
    }),
    []
  );

  useEffect(() => {
    if (!window.BroadcastChannel) {
      return;
    }

    const bc = new window.BroadcastChannel("aws-waf-captcha");
    bc.onmessage = ev => {
      onGetToken(ev.data);
    };

    broadcastChannel.current = bc;

    return () => {
      bc.close();
    };
  }, []);

  function handleDivCallback(div: HTMLDivElement | null) {
    if (hasRendered || !open || !div) {
      return;
    }

    setHasRendered(true);

    window.AwsWafCaptcha.renderCaptcha(div, {
      apiKey: process.env.REACT_APP_CAPTCHA_API_KEY!,
      onSuccess(wafToken: string) {
        localStorage.setItem("wafToken", wafToken);

        if (broadcastChannel.current) {
          broadcastChannel.current.postMessage(wafToken);
        }

        onGetToken(wafToken);
      },
      onError(err) {
        console.error("Captcha error", err);
        setError(err.message);
      }
    });
  }

  return (
    <CaptchaContext.Provider value={ctxValue}>
      <Dialog open={open}>
        <DialogTitle>Security Challenge</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error">{error}</Alert>}
          <div ref={handleDivCallback} />
        </DialogContent>
      </Dialog>
      {children}
    </CaptchaContext.Provider>
  );
}
